import * as React from "react"
import { ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react"

import { cn } from "../../lib/utils"

/**
 * Pagination components, restyled for the Institutional Console redesign
 * (DESIGN-SPEC.md §4.3 "Pagination: `1–15 of 25` mono left; Prev/1/2/Next
 * pills right"). This file only supplies the pill-style Prev/page-number/
 * Next controls (`PaginationLink`/`PaginationPrevious`/`PaginationNext`) and
 * a new `PaginationSummary` for the mono "x–y of N" text; the surrounding
 * `<nav>`/`<ul>`/`<li>` structural components are unchanged so existing
 * callers (e.g. Holdings.jsx) that compose `Pagination > PaginationContent >
 * PaginationItem > PaginationLink` keep working without any call-site
 * changes — only the pill's visual treatment changed (previously reused
 * `buttonVariants`, which produced 30px/40px control-height buttons; now a
 * dedicated compact pill per the mockup's `padding: 4px 10px; border-radius:
 * 5px; font-size: 12px` geometry, distinct from the general Button scale).
 */

const Pagination = ({
  className,
  ...props
}) => (
  <nav
    role="navigation"
    aria-label="pagination"
    className={cn("mx-auto flex w-full justify-center", className)}
    {...props}
  />
)
Pagination.displayName = "Pagination"

const PaginationContent = React.forwardRef(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn("flex flex-row items-center gap-1", className)}
    {...props}
  />
))
PaginationContent.displayName = "PaginationContent"

const PaginationItem = React.forwardRef(({ className, ...props }, ref) => (
  <li ref={ref} className={cn("", className)} {...props} />
))
PaginationItem.displayName = "PaginationItem"

// §4.3 pagination pill: mono, 12px, 4px/10px padding, 5px radius. Active
// page pill matches the filter-pill "active" treatment (--accent-tint bg +
// --accent-text, 500 weight); inactive pills use --text-muted with a faint
// hover background; kept a real `<button>` with `min-h-[44px]` touch target
// padding on touch/mobile per §7 "44px touch targets on mobile via expanded
// padding" (visually a smaller 12px-padded target on pointer/desktop).
const PaginationLink = ({
  className,
  isActive,
  size,
  ...props
}) => (
  <button
    type="button"
    aria-current={isActive ? "page" : undefined}
    className={cn(
      "inline-flex min-w-[26px] items-center justify-center whitespace-nowrap rounded-[5px] px-2.5 py-1 font-mono text-xs tabular-nums transition-colors duration-[120ms] ease-out",
      "min-h-[44px] sm:min-h-0", // 44px touch target on mobile (§7); collapses on larger pointers
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
      isActive
        ? "bg-accent-tint font-medium text-[var(--accent-text,var(--color-link))]"
        : "text-[var(--text-muted,var(--color-text-tertiary))] hover:bg-[rgba(255,255,255,0.04)]",
      className
    )}
    {...props}
  />
)
PaginationLink.displayName = "PaginationLink"

const PaginationPrevious = ({
  className,
  ...props
}) => (
  <PaginationLink
    aria-label="Go to previous page"
    className={cn("gap-1 px-2.5", className)}
    {...props}
  >
    <ChevronLeft className="h-3.5 w-3.5" />
    <span className="hidden sm:inline">Prev</span>
  </PaginationLink>
)
PaginationPrevious.displayName = "PaginationPrevious"

const PaginationNext = ({
  className,
  ...props
}) => (
  <PaginationLink
    aria-label="Go to next page"
    className={cn("gap-1 px-2.5", className)}
    {...props}
  >
    <span className="hidden sm:inline">Next</span>
    <ChevronRight className="h-3.5 w-3.5" />
  </PaginationLink>
)
PaginationNext.displayName = "PaginationNext"

const PaginationEllipsis = ({
  className,
  ...props
}) => (
  <span
    aria-hidden
    className={cn("flex h-9 w-9 items-center justify-center", className)}
    {...props}
  >
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More pages</span>
  </span>
)
PaginationEllipsis.displayName = "PaginationEllipsis"

/**
 * PaginationSummary - mono "1–15 of 25" left-hand label (§4.3).
 * New component; additive only, does not replace any existing element.
 */
const PaginationSummary = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      "font-mono text-[11px] tabular-nums text-[var(--text-faint,var(--color-text-disabled))]",
      className
    )}
    {...props}
  />
))
PaginationSummary.displayName = "PaginationSummary"

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationSummary,
}
